/**
 * Recording Cache
 *
 * IndexedDB-based cache for downloaded recordings with download queue management. Supports progress tracking and priority downloading for freshly merged recordings.
 */

import { IS_DEV } from '~/lib/constants'

const logger = console.scope('RecordingCache')

// IndexedDB configuration
const DB_NAME = 'oylo-recording-cache'
const DB_VERSION = 2
const STORE_RECORDINGS = 'recordings'
const STORE_THUMBS = 'thumbnails'

// Download queue state
let dbInstance = null
let dbInitPromise = null
const downloadQueue = []
let isDownloading = false
let currentDownload = null
let abortController = null
const reportedErrors = new Set()

// Progress listeners: recordingId -> Set<callback>
const progressListeners = new Map()

// Cache state listeners: callback[]
const cacheStateListeners = new Set()

// In-memory cache state: recordingId -> { status, progress, blobUrl }
const cacheState = new Map()

/**
 * Initialize the recording cache database
 */
async function openDatabase() {
	if (dbInstance) return dbInstance
	if (dbInitPromise) return dbInitPromise

	dbInitPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION)

		request.onerror = () => {
			logger.error('Failed to open recording cache database:', request.error)
			dbInitPromise = null
			reject(request.error)
		}

		request.onsuccess = () => {
			dbInstance = request.result
			dbInstance.onclose = () => {
				dbInstance = null
				dbInitPromise = null
			}
			resolve(dbInstance)
		}

		request.onupgradeneeded = event => {
			const db = event.target.result

			if (!db.objectStoreNames.contains(STORE_RECORDINGS)) {
				const store = db.createObjectStore(STORE_RECORDINGS, { keyPath: 'id' })
				store.createIndex('timestamp', 'timestamp', { unique: false })
				store.createIndex('size', 'size', { unique: false })
			}

			// Thumbnails are kept in their own store so clearing a downloaded video ("remove from
			// device") never costs the poster image the gallery grid needs to stay usable offline.
			if (!db.objectStoreNames.contains(STORE_THUMBS)) {
				db.createObjectStore(STORE_THUMBS, { keyPath: 'id' })
			}
		}
	})

	return dbInitPromise
}

/**
 * Check if a recording is cached
 * @param {string} recordingId
 * @returns {Promise<boolean>}
 */
export async function isRecordingCached(recordingId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_RECORDINGS, 'readonly')
			const store = tx.objectStore(STORE_RECORDINGS)
			const request = store.count(IDBKeyRange.only(recordingId))

			request.onsuccess = () => resolve(request.result > 0)
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('isRecordingCached error:', err)
		return false
	}
}

/**
 * Get a cached recording
 * @param {string} recordingId
 * @returns {Promise<{ blob: Blob, filename: string } | null>}
 */
export async function getCachedRecording(recordingId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_RECORDINGS, 'readonly')
			const store = tx.objectStore(STORE_RECORDINGS)
			const request = store.get(recordingId)

			request.onsuccess = () => {
				const record = request.result
				if (record) {
					resolve({ blob: record.blob, filename: record.filename })
				} else {
					resolve(null)
				}
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getCachedRecording error:', err)
		return null
	}
}

/**
 * Store a recording in cache
 * @param {string} recordingId
 * @param {Blob} blob
 * @param {string} filename
 */
async function storeRecording(recordingId, blob, filename) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_RECORDINGS, 'readwrite')
			const store = tx.objectStore(STORE_RECORDINGS)

			const record = {
				id: recordingId,
				blob,
				filename,
				size: blob.size,
				timestamp: Date.now(),
			}

			const request = store.put(record)
			request.onsuccess = () => resolve()
			request.onerror = () => {
				const err = request.error
				if (err?.name === 'QuotaExceededError') {
					updateCacheState(recordingId, 'quota_error', 0)
					reject(err)
				} else {
					reject(err)
				}
			}

			tx.onabort = () => {
				if (tx.error?.name === 'QuotaExceededError') {
					updateCacheState(recordingId, 'quota_error', 0)
				}
				reject(tx.error)
			}
		})
	} catch (err) {
		logger.error('storeRecording error:', err)
		throw err
	}
}

/**
 * Remove a recording from cache
 * @param {string} recordingId
 */
export async function removeRecording(recordingId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_RECORDINGS, 'readwrite')
			const store = tx.objectStore(STORE_RECORDINGS)
			const request = store.delete(recordingId)

			request.onsuccess = () => {
				cacheState.delete(recordingId)
				notifyCacheStateChange()
				resolve()
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('removeRecording error:', err)
	}
}

/**
 * Fetch and persist a recording's thumbnail. Best-effort: a failure just means the grid falls back
 * to loading the CDN URL directly.
 * @param {string} recordingId
 * @param {string} url
 * @returns {Promise<Blob | null>}
 */
export async function cacheThumbnail(recordingId, url) {
	if (!url) return null
	try {
		const res = await fetch(url, { credentials: 'include' })
		if (!res.ok) throw new Error(`HTTP ${res.status}`)
		const blob = await res.blob()

		const db = await openDatabase()
		await new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBS, 'readwrite')
			const request = tx.objectStore(STORE_THUMBS).put({ id: recordingId, blob, timestamp: Date.now() })
			request.onsuccess = () => resolve()
			request.onerror = () => reject(request.error)
		})
		return blob
	} catch (err) {
		logger.warn(`cacheThumbnail failed for ${recordingId}:`, err.message)
		return null
	}
}

/**
 * Get every cached thumbnail as object URLs, keyed by recording id. Caller owns the URLs and must
 * revoke them (see revokeThumbnailUrls).
 * @returns {Promise<Map<string, string>>}
 */
export async function getCachedThumbnailUrls() {
	try {
		const db = await openDatabase()
		return await new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBS, 'readonly')
			const request = tx.objectStore(STORE_THUMBS).getAll()
			request.onsuccess = () => {
				const map = new Map()
				for (const rec of request.result) {
					if (rec?.blob) map.set(rec.id, URL.createObjectURL(rec.blob))
				}
				resolve(map)
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getCachedThumbnailUrls error:', err)
		return new Map()
	}
}

/**
 * Revoke object URLs handed out by getCachedThumbnailUrls
 * @param {Map<string, string>} urlMap
 */
export function revokeThumbnailUrls(urlMap) {
	if (!urlMap) return
	for (const url of urlMap.values()) URL.revokeObjectURL(url)
}

/**
 * Delete a cached thumbnail (used when the recording itself is deleted server-side)
 * @param {string} recordingId
 */
export async function removeRecordingThumbnail(recordingId) {
	try {
		const db = await openDatabase()
		await new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBS, 'readwrite')
			const request = tx.objectStore(STORE_THUMBS).delete(recordingId)
			request.onsuccess = () => resolve()
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('removeRecordingThumbnail error:', err)
	}
}

/**
 * Get recording cache state
 * @param {string} recordingId
 * @returns {{ status: 'none' | 'queued' | 'downloading' | 'cached' | 'uncacheable' | 'error', progress: number }}
 */
export function getRecordingState(recordingId) {
	return cacheState.get(recordingId) || { status: 'none', progress: 0 }
}

/**
 * Get all cache states
 * @returns {Map<string, { status: string, progress: number }>}
 */
export function getAllRecordingCacheStates() {
	return new Map(cacheState)
}

/**
 * Subscribe to cache state changes
 * @param {function} callback
 * @returns {function} Unsubscribe function
 */
export function subscribeToRecordingCacheState(callback) {
	cacheStateListeners.add(callback)
	return () => cacheStateListeners.delete(callback)
}

/**
 * Subscribe to progress updates for a specific recording
 * @param {string} recordingId
 * @param {function(number)} callback - Called with progress 0-1
 * @returns {function} Unsubscribe function
 */
export function subscribeToProgress(recordingId, callback) {
	if (!progressListeners.has(recordingId)) {
		progressListeners.set(recordingId, new Set())
	}
	progressListeners.get(recordingId).add(callback)
	return () => {
		const listeners = progressListeners.get(recordingId)
		listeners?.delete(callback)
		if (listeners?.size === 0) {
			progressListeners.delete(recordingId)
		}
	}
}

/**
 * Notify progress listeners
 */
function notifyProgress(recordingId, progress) {
	const listeners = progressListeners.get(recordingId)
	if (listeners) {
		listeners.forEach(cb => cb(progress))
	}
}

/**
 * Notify cache state listeners
 */
function notifyCacheStateChange() {
	cacheStateListeners.forEach(cb => cb(new Map(cacheState)))
}

/**
 * Update cache state for a recording
 */
function updateCacheState(recordingId, status, progress = 0) {
	cacheState.set(recordingId, { status, progress })
	notifyProgress(recordingId, progress)
	notifyCacheStateChange()
}

/**
 * Queue a recording for download
 * @param {object} recording - Recording object with id, sessionId, name, start
 * @param {boolean} priority - If true, download immediately (for freshly merged)
 * @returns {Promise<boolean>} Resolves true once cached, false if the download failed. An aborted
 *   download does not settle — the item is re-queued and the waiter rides the next attempt.
 */
export function queueDownload(recording, priority = false) {
	const { id: recordingId } = recording

	// Skip if already cached or downloading
	const state = getRecordingState(recordingId)
	if (state.status === 'cached') return Promise.resolve(true)
	if (state.status === 'downloading') return waitFor(recordingId)

	// Remove from queue if already there (will re-add with new priority)
	const existingIndex = downloadQueue.findIndex(item => item.recording.id === recordingId)
	if (existingIndex !== -1) {
		downloadQueue.splice(existingIndex, 1)
	}

	// Add to queue
	const queueItem = { recording, priority, timestamp: Date.now() }

	if (priority) {
		// Priority items go to front of queue
		downloadQueue.unshift(queueItem)
		updateCacheState(recordingId, 'queued', 0)

		// If currently downloading a non-priority item, abort it
		if (isDownloading && currentDownload && !currentDownload.priority) {
			logger.info(`Interrupting download for priority recording ${recordingId}`)
			abortController?.abort()
		}
	} else {
		downloadQueue.push(queueItem)
		updateCacheState(recordingId, 'queued', 0)
	}

	// Start processing queue
	const waiter = waitFor(recordingId)
	processQueue()
	return waiter
}

// Callers awaiting a download's terminal outcome: recordingId -> Array<(ok: boolean) => void>
const downloadWaiters = new Map()

function waitFor(recordingId) {
	return new Promise(resolve => {
		const list = downloadWaiters.get(recordingId) || []
		list.push(resolve)
		downloadWaiters.set(recordingId, list)
	})
}

function settleWaiters(recordingId, ok) {
	const list = downloadWaiters.get(recordingId)
	if (!list) return
	downloadWaiters.delete(recordingId)
	for (const resolve of list) resolve(ok)
}

/**
 * Queue multiple recordings for download (newest first)
 * @param {array} recordings - Array of recording objects
 */
export function queueDownloads(recordings) {
	const sorted = [...recordings].sort((a, b) => b.start - a.start)

	for (const recording of sorted) {
		queueDownload(recording, false)
	}
}

/**
 * Process the download queue
 */
async function processQueue() {
	if (isDownloading || downloadQueue.length === 0) return

	isDownloading = true
	const item = downloadQueue.shift()
	currentDownload = item

	const { recording, priority } = item
	const { id: recordingId, start, downloadUrl: url } = recording

	logger.info(`Starting download: ${recordingId} (priority: ${priority})`)
	updateCacheState(recordingId, 'downloading', 0)

	try {
		if (!url) {
			throw new Error('No download URL available')
		}

		// Download with progress tracking
		abortController = new AbortController()
		const blob = await downloadWithProgress(url, recordingId, abortController.signal)

		// Generate filename: recording_YYYYMMDD_HHMMSS.mp4
		const date = new Date(start)
		const dateStr = date.toISOString().replace(/[-:]/g, '').replace('T', '_').slice(0, 15)
		const filename = `recording_${dateStr}.mp4`

		// Store in cache
		await storeRecording(recordingId, blob, filename)

		logger.info(`Downloaded and cached: ${recordingId} (${formatBytes(blob.size)})`)
		updateCacheState(recordingId, 'cached', 1)
		settleWaiters(recordingId, true)
	} catch (err) {
		if (err.name === 'AbortError') {
			logger.info(`Download aborted: ${recordingId}`)
			// Re-queue at front if aborted for priority. Waiters stay pending on purpose — this item
			// runs again, and settling here would report a failure that never happened.
			downloadQueue.unshift(item)
			updateCacheState(recordingId, 'queued', 0)
		} else if (err.message === 'CORS_BLOCKED') {
			// CORS prevents caching - mark as uncacheable but still usable via direct download
			logger.warn(`Download blocked by CORS: ${recordingId}`)
			updateCacheState(recordingId, 'uncacheable', 0)
			settleWaiters(recordingId, false)
		} else {
			logger.error(`Download failed: ${recordingId}`, err)
			updateCacheState(recordingId, 'error', 0)
			settleWaiters(recordingId, false)
		}

		// Report each error type once per session for Rollbar
		if (err.name !== 'AbortError' && !reportedErrors.has(err.message)) {
			reportedErrors.add(err.message)
			setTimeout(() => {
				throw err
			}, 0)
		}

		// In dev mode, stop queue on any error
		if (IS_DEV && err.name !== 'AbortError') {
			logger.warn('Dev mode: stopping download queue after error')
			for (const dropped of downloadQueue) settleWaiters(dropped.recording.id, false)
			downloadQueue.length = 0
		}
	}

	isDownloading = false
	currentDownload = null
	abortController = null

	// Continue with next item
	processQueue()
}

/**
 * Download a file with progress tracking Falls back to no-cors download if fetch fails due to CORS
 * @param {string} url
 * @param {string} recordingId
 * @param {AbortSignal} signal
 * @returns {Promise<Blob>}
 */
async function downloadWithProgress(url, recordingId, signal) {
	try {
		const response = await fetch(url, { signal, credentials: 'include' })

		if (!response.ok) {
			throw new Error(`HTTP ${response.status}`)
		}

		const contentLength = response.headers.get('content-length')
		const total = contentLength ? parseInt(contentLength, 10) : 0

		if (!response.body) {
			// Fallback for browsers without ReadableStream
			const blob = await response.blob()
			updateCacheState(recordingId, 'downloading', 1)
			return blob
		}

		const reader = response.body.getReader()
		const chunks = []
		let received = 0

		while (true) {
			const { done, value } = await reader.read()

			if (done) break

			chunks.push(value)
			received += value.length

			if (total > 0) {
				const progress = received / total
				updateCacheState(recordingId, 'downloading', progress)
			}
		}

		const blob = new Blob(chunks, { type: 'video/mp4' })
		return blob
	} catch (err) {
		// CORS error - can't use fetch for progress tracking Fall back to XMLHttpRequest which can report progress without CORS
		if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
			logger.info(`Fetch failed for ${recordingId}, trying XHR fallback`)
			return downloadWithXHR(url, recordingId, signal)
		}
		throw err
	}
}

/**
 * Download using XMLHttpRequest (fallback for CORS issues) XHR can download cross-origin files when used correctly
 */
function downloadWithXHR(url, recordingId, signal) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest()
		xhr.open('GET', url, true)
		xhr.responseType = 'blob'
		xhr.withCredentials = true

		xhr.onprogress = event => {
			if (event.lengthComputable) {
				const progress = event.loaded / event.total
				updateCacheState(recordingId, 'downloading', progress)
			}
		}

		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve(xhr.response)
			} else {
				reject(new Error(`HTTP ${xhr.status}`))
			}
		}

		xhr.onerror = () => {
			// XHR also blocked - need to use anchor tag download (no caching possible)
			logger.warn(`XHR also failed for ${recordingId}, download not cacheable`)
			reject(new Error('CORS_BLOCKED'))
		}

		xhr.onabort = () => {
			reject(new DOMException('Aborted', 'AbortError'))
		}

		if (signal) {
			signal.addEventListener('abort', () => xhr.abort())
		}

		xhr.send()
	})
}

/**
 * Get a cached recording's blob URL for in-app playback
 * @param {string} recordingId
 * @returns {Promise<{ blobUrl: string, filename: string, revoke: () => void } | null>}
 */
export async function getRecordingForPlayback(recordingId) {
	const cached = await getCachedRecording(recordingId)
	if (!cached) {
		logger.warn('Recording not cached:', recordingId)
		return null
	}

	const { blob, filename } = cached
	const blobUrl = URL.createObjectURL(blob)

	return {
		blobUrl,
		filename,
		revoke: () => URL.revokeObjectURL(blobUrl),
	}
}

/**
 * Initialize cache states from IndexedDB
 */
export async function initRecordingCacheStates() {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_RECORDINGS, 'readonly')
			const store = tx.objectStore(STORE_RECORDINGS)
			const request = store.getAllKeys()

			request.onsuccess = () => {
				const ids = request.result
				ids.forEach(id => {
					cacheState.set(id, { status: 'cached', progress: 1 })
				})
				notifyCacheStateChange()
				resolve()
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('initRecordingCacheStates error:', err)
	}
}

/**
 * Get cache statistics
 */
export async function getRecordingCacheStats() {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_RECORDINGS, 'readonly')
			const store = tx.objectStore(STORE_RECORDINGS)
			const request = store.openCursor()
			let count = 0
			let totalSize = 0

			request.onsuccess = event => {
				const cursor = event.target.result
				if (cursor) {
					count++
					totalSize += cursor.value.size || 0
					cursor.continue()
				} else {
					resolve({ count, totalSize, formattedSize: formatBytes(totalSize) })
				}
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getRecordingCacheStats error:', err)
		return { count: 0, totalSize: 0, formattedSize: '0 B' }
	}
}

/**
 * Format bytes to human readable
 */
function formatBytes(bytes) {
	if (bytes === 0) return '0 B'
	const k = 1024
	const sizes = ['B', 'KB', 'MB', 'GB']
	const i = Math.floor(Math.log(bytes) / Math.log(k))
	return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}
